import DOMPurify from "dompurify";
import React from "react";
import { RawContent } from "./RawContent";

/**
 * Renders stored rich text (editor HTML written by users, admins or
 * partners) safely (SEC-12).
 *
 * DOMPurify keeps formatting, links, images and inline styles, and removes
 * scripts, event handlers, `javascript:` URLs, iframes, forms and anything
 * else that could run code. Links that open a new tab get
 * rel="noopener noreferrer".
 *
 * Use this for any HTML that came from the database. Never put stored HTML
 * in dangerouslySetInnerHTML or an HTML-to-React parser directly.
 */

let hooked = false;

function addHooks() {
  if (hooked) return;
  hooked = true;
  DOMPurify.addHook("afterSanitizeAttributes", (node: Element) => {
    if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
}

const CONFIG = {
  USE_PROFILES: { html: true },
  ADD_ATTR: ["target"],
  FORBID_TAGS: ["style", "form", "input", "button", "textarea", "select"],
};

export function sanitizeHtml(html: string | null | undefined): string {
  addHooks();
  return DOMPurify.sanitize(html ?? "", CONFIG) as string;
}

type SafeHtmlProps = {
  html: string | null | undefined;
  className?: string;
} & React.ComponentProps<typeof RawContent>;

/** RawContent's typography, filled with sanitised HTML. */
export const SafeHtml = ({ html, ...rest }: SafeHtmlProps) => (
  <RawContent
    {...rest}
    // The one sanctioned HTML sink: input is DOMPurify-sanitised above.
    // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
    dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
  />
);
